import type { ReactNode } from 'react'
import type { FindingsReport } from '../../lib/api.ts'
import { formatCount, formatRate } from '../../lib/findingsReport.ts'
import { CheckCell } from '../../lib/findingsList.ts'
import {
  confirmedErrorRates,
  RESPONSE_COLOR,
  RESPONSE_HELP,
} from '../../lib/findingsResponsePath.ts'
import { FOCUS_RING } from '../focusRing.ts'
import { FindingsHelp, type FindingsHelpContent } from './FindingsHelp.tsx'

function Ring({ count, base, color }: { count: number; base: number; color: string }) {
  const share = base === 0 ? 0 : Math.max(0, Math.min(1, count / base))
  return (
    <span
      aria-hidden="true"
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
      style={{
        background: `conic-gradient(${color} ${share * 360}deg, var(--color-fill-weak) 0deg)`,
      }}
    >
      <span className="bg-background-raised h-8 w-8 rounded-full" />
    </span>
  )
}

function FlowNode({
  cell,
  label,
  count,
  base,
  basis,
  color,
  note,
  selected,
  onExplore,
  children,
}: {
  cell: CheckCell
  label: string
  count: number
  base: number
  basis: string
  color: string
  note?: string
  selected?: CheckCell | undefined
  onExplore: (cell: CheckCell) => void
  children?: ReactNode
}) {
  const rate = formatRate(base === 0 ? undefined : count / base)
  return (
    <div
      data-flow-node={cell}
      className={`bg-background-raised border-stroke-weak min-w-0 rounded-xl border p-3 ${selected === cell ? 'ring-stroke-brand-strong ring-2' : ''}`}
    >
      <FindingsHelp
        label={label}
        content={{
          ...RESPONSE_HELP[cell]!,
          calculation: `${RESPONSE_HELP[cell]!.calculation} Percentual: ${formatCount(count)} ÷ ${formatCount(base)} ${basis}.`,
        }}
        className="text-tSmall text-text-weak font-medium"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <button
          type="button"
          disabled={count === 0}
          onClick={() => onExplore(cell)}
          aria-pressed={selected === cell}
          aria-label={`Explorar ${label}: ${formatCount(count)} por verificação`}
          className={`text-text-strong cursor-pointer rounded-md text-3xl font-semibold tabular-nums disabled:cursor-default hover:enabled:text-text-brand ${FOCUS_RING}`}
        >
          {formatCount(count)}
        </button>
        <Ring count={count} base={base} color={color} />
      </div>
      <p className="text-tSmall text-text-weak mt-2 tabular-nums">
        <strong className="text-text-strong">{rate}</strong> de {formatCount(base)} {basis}
      </p>
      {note !== undefined && <p className="text-text-weak mt-1 text-[11px]">{note}</p>}
      {children}
    </div>
  )
}

const auditedHelp: FindingsHelpContent = {
  calculation: 'Acertos confirmados + erros confirmados, dentro das contestações.',
  source:
    'Triagem humana das contestações nas conferências por dia de entrada na conferência no período.',
  includes:
    'Somente divergências contestadas com parecer humano. Cada apontamento conta uma vez por conferência, pela resposta válida mais recente; o período seleciona o dia de entrada na conferência.',
  excludes:
    'Não é o total de feedbacks ou comentários. Itens não avaliados pela comparação, sem triagem, concordâncias operacionais e sem resposta ficam fora.',
  provisional:
    'A fila de feedbacks pode incluir respostas salvas antes de concluir. Este painel conta apontamentos distintos, por isso os totais da fila e do painel podem diferir.',
}
const errorHelp = (basis: 'total' | 'responded'): FindingsHelpContent => ({
  calculation:
    basis === 'total'
      ? 'Erros confirmados ÷ total de divergências.'
      : 'Erros confirmados ÷ divergências respondidas.',
  source: 'Triagem humana das contestações.',
  includes:
    basis === 'total'
      ? 'A base operacional inclui todos os apontamentos, respondidos ou não.'
      : 'A base operacional inclui concordâncias e contestações, triadas ou não.',
  excludes:
    'Somente erros triados entram no numerador. Estas taxas operacionais e seus complementos não representam acerto ou acurácia confirmados.',
})

/** Results are nested under contestations, which are themselves part of saved responses. */
export function FindingsFlow({
  report,
  selected,
  onExplore,
}: {
  report: FindingsReport
  selected?: CheckCell | undefined
  onExplore: (cell: CheckCell) => void
}) {
  const r = report.responses
  const audited = r.confirmed + r.errors
  const triageBase = r.disputed
  const triageBasis = 'contestações'
  const errorRates = confirmedErrorRates(r)
  const shared = { selected, onExplore }
  return (
    <section
      className="bg-background-raised border-stroke-weak rounded-2xl border p-5 shadow-sm"
      aria-label="Fluxo dos apontamentos"
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-tPSemi text-text-strong">Apontamentos, respostas e triagem</h2>
        <p className="text-tSmall text-text-weak">
          Selecione uma contagem para explorar por verificação.
        </p>
      </div>
      <div className="grid gap-5 lg:grid-cols-[0.8fr_1fr_2.2fr] lg:items-start">
        <div>
          <p className="text-text-weak mb-3 text-[10px] font-semibold uppercase tracking-[0.14em]">
            01 · Comparação
          </p>
          <FlowNode
            cell={CheckCell.Total}
            label="Apontamentos"
            count={r.total}
            base={r.total}
            basis="apontamentos"
            color={RESPONSE_COLOR.total}
            {...shared}
          />
          <div className="text-tSmall text-text-weak mt-3">
            <FindingsHelp
              label="Conferências com apontamento"
              content={{
                calculation: `${formatCount(report.engagement.conferencesWithFindings)} conferências com divergência ÷ ${formatCount(report.verifications.total)} conferências do recorte de entrada.`,
                source: 'Conferências e resultados atuais ou respondidos no histórico.',
                includes: 'Cada conferência conta uma vez.',
                excludes: 'Itens não avaliados, informativos ou sem divergência.',
              }}
            >
              Conferências com apontamento: {formatCount(report.engagement.conferencesWithFindings)}{' '}
              (
              {formatRate(
                report.verifications.total === 0
                  ? undefined
                  : report.engagement.conferencesWithFindings / report.verifications.total,
              )}
              )
            </FindingsHelp>
          </div>
        </div>
        <section
          aria-label="Respostas do total de apontamentos"
          data-flow-parent="total"
          className="border-stroke-weak relative border-l-2 pl-4 before:absolute before:top-24 before:-left-5 before:hidden before:w-5 before:border-t before:border-stroke-weak lg:before:block"
        >
          <p className="text-text-weak mb-3 text-[10px] font-semibold uppercase tracking-[0.14em]">
            02 · Dos {formatCount(r.total)} apontamentos
          </p>
          <div className="grid gap-3">
            <FlowNode
              cell={CheckCell.Responded}
              label="Respondidos"
              count={r.responded}
              base={r.total}
              basis="apontamentos"
              color={RESPONSE_COLOR.responded}
              {...shared}
            />
            <FlowNode
              cell={CheckCell.Unanswered}
              label="Sem resposta"
              count={r.unanswered}
              base={r.total}
              basis="apontamentos"
              color={RESPONSE_COLOR.unanswered}
              {...shared}
            />
          </div>
        </section>
        <section
          aria-label="Respostas e resultado das contestações"
          data-flow-parent="responded"
          className="border-stroke-weak relative border-l-2 pl-4 before:absolute before:top-24 before:-left-5 before:hidden before:w-5 before:border-t before:border-stroke-weak lg:before:block"
        >
          <p className="text-text-weak mb-3 text-[10px] font-semibold uppercase tracking-[0.14em]">
            03 · Dos {formatCount(r.responded)} respondidos
          </p>
          <div className="grid gap-3 xl:grid-cols-[1fr_1.15fr]">
            <FlowNode
              cell={CheckCell.Agreed}
              label="Concordaram"
              count={r.agreed}
              base={r.responded}
              basis="respondidos"
              note="Concordância operacional"
              color={RESPONSE_COLOR.agreed}
              {...shared}
            />
            <section
              aria-label="Contestações e resultado da triagem"
              data-flow-parent="disputed"
              className="col-span-full grid gap-3 xl:grid-cols-[1fr_1.15fr]"
            >
              <FlowNode
                cell={CheckCell.Disputed}
                label="Contestaram"
                count={r.disputed}
                base={r.responded}
                basis="respondidos"
                color={RESPONSE_COLOR.disputed}
                {...shared}
              />
              <div
                className="border-stroke-weak border-l-2 pl-3"
                style={{ borderColor: RESPONSE_COLOR.disputed }}
              >
                <h3 className="text-tSmall text-text-strong mb-1 font-semibold">
                  Resultado da triagem
                </h3>
                <p className="text-text-weak mb-3 text-[11px]">
                  Das {formatCount(triageBase)} contestações
                </p>
                <section
                  aria-label="Apontamentos avaliados"
                  data-flow-parent="triaged"
                  className="bg-fill-weaker border-stroke-weak rounded-xl border p-3"
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <FindingsHelp label="Apontamentos avaliados" content={auditedHelp}>
                      <strong className="text-text-strong text-lg tabular-nums">
                        {formatCount(audited)}
                      </strong>{' '}
                      apontamentos avaliados
                      <span className="text-text-weak block text-[11px]">
                        Contestações com parecer humano
                      </span>
                      <span className="text-text-weak block text-[11px] tabular-nums">
                        {formatRate(triageBase === 0 ? undefined : audited / triageBase)} de{' '}
                        {formatCount(triageBase)} contestações
                      </span>
                    </FindingsHelp>
                    <Ring count={audited} base={triageBase} color={RESPONSE_COLOR.disputed} />
                  </div>
                  <div className="grid gap-3">
                    <FlowNode
                      cell={CheckCell.Confirmed}
                      label="Acertos confirmados"
                      count={r.confirmed}
                      base={audited}
                      basis="avaliados"
                      color={RESPONSE_COLOR.confirmed}
                      {...shared}
                    />
                    <FlowNode
                      cell={CheckCell.Error}
                      label="Erros confirmados"
                      count={r.errors}
                      base={audited}
                      basis="avaliados"
                      color={RESPONSE_COLOR.errors}
                      {...shared}
                    >
                      <div className="border-stroke-weak text-tSmall text-text-weak mt-3 grid gap-2 border-t pt-2 tabular-nums">
                        {(
                          [
                            { base: r.total, basis: 'total', name: 'apontamentos' },
                            { base: r.responded, basis: 'responded', name: 'respondidos' },
                          ] as const
                        ).map(({ base, basis, name }) => (
                          <FindingsHelp
                            key={basis}
                            label={`Erros confirmados sobre ${name}`}
                            content={errorHelp(basis)}
                          >
                            <strong className="text-text-strong">
                              {formatRate(errorRates[basis])}
                            </strong>{' '}
                            de {formatCount(base)} {name}
                          </FindingsHelp>
                        ))}
                      </div>
                    </FlowNode>
                  </div>
                </section>
                <div className="mt-3">
                  <FlowNode
                    cell={CheckCell.PendingConclusion}
                    label="Não chegaram à triagem"
                    count={r.pendingConclusion}
                    base={triageBase}
                    basis={triageBasis}
                    color={RESPONSE_COLOR.pendingConclusion}
                    {...shared}
                  />
                </div>
                <div className="mt-3">
                  <FlowNode
                    cell={CheckCell.AwaitingTriage}
                    label="Aguardando triagem"
                    count={r.awaitingTriage}
                    base={triageBase}
                    basis={triageBasis}
                    color={RESPONSE_COLOR.awaitingTriage}
                    {...shared}
                  />
                </div>
              </div>
            </section>
          </div>
        </section>
      </div>
      {r.total === 0 && (
        <p className="text-tSmall text-text-weak mt-4">Sem apontamentos no período.</p>
      )}
    </section>
  )
}
